<!--
	Ported from analytics-design/src/components/BrandsDashboard.tsx, then rewired to real data -
	see the component's own comment for what changed. "Manage Brands" is hidden (2026-10-06,
	product decision 2026-10-05: not in v1), see the @if (false) blocks below.

	SETTE redesign (CLIPPING-598): token-by-token restyle, "Reach" -> "Visibility" in all copy.
	The three sign-color decisions (Net Sentiment pill, impactful-story Impact pill, Rate of
	Change cells) moved from `.startsWith('+'/'-')` string-sniffing to real Tone fields computed
	in the component - the formatted strings now carry U+2212 for negatives, which that string
	check would have silently mis-colored.
-->
<div class="space-y-4">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="3" [chartCount]="2"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetch()"></app-analytics-error-state>
	} @else if (!hasCompetitors) {
	<app-analytics-empty-state variant="no-competitors"></app-analytics-empty-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- 1. TOP HEADER ROW -->
	<div class="bg-surface px-4 py-3 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-3">
		<div class="flex flex-col sm:flex-row sm:items-center gap-3">
			<div>
				<div class="flex items-center gap-2">
					<h1 class="overline-label text-ink">{{ 'ANALYTICS_GLOSSARY.BRANDS' | translate }}</h1>
					<span class="bg-blue-tint text-blue font-medium px-2 py-0.5 rounded-full text-[11px]">{{ 'ANALYTICS_BRANDS.MONITORED_BRANDS_COUNT' | translate : { count: monitoredBrandsCount } }}</span>
				</div>
				<p class="text-ink-subtle text-xs mt-0.5">{{ 'ANALYTICS_BRANDS.SUBTITLE' | translate }}</p>
			</div>
		</div>

		<div class="flex items-center gap-3 caption text-ink-subtle font-normal shrink-0">
			<span class="text-ink-subtle font-medium hidden sm:inline">{{ 'ANALYTICS_BRANDS.ROLE_LABEL' | translate }}:</span>
			<div class="flex items-center gap-1.5">
				<span class="w-2.5 h-2.5 rounded-sm bg-blue shrink-0 inline-block"></span>
				<span class="font-medium text-ink">{{ 'ANALYTICS_BRANDS.OUR_BRAND_CLIENT' | translate }}</span>
			</div>
			<div class="flex items-center gap-1.5 ml-1">
				<span class="w-2.5 h-2.5 rounded-sm bg-ink-subtle shrink-0 inline-block"></span>
				<span class="font-medium text-ink-muted">{{ 'ANALYTICS_BRANDS.COMPETITION_COMPETITORS' | translate }}</span>
			</div>
			<!-- Hidden 2026-10-06 (product decision 2026-10-05): not in v1. Button + modal kept, not deleted. -->
			@if (false) {
			<button type="button" (click)="openManageModal()" class="text-blue hover:underline font-medium flex items-center gap-1 ml-2 cursor-pointer">
				<span>Manage Brands</span>
				<mat-icon class="!w-3 !h-3 !text-xs">open_in_new</mat-icon>
			</button>
			}
		</div>
	</div>

	<!-- 2. KPI CARDS (6 columns) -->
	<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
		<app-kpi-tile [label]="'ANALYTICS_BRANDS.TOTAL_MENTIONS_ALL_BRANDS' | translate" infoKey="ANALYTICS_INFO.BRANDS.TOTAL_MENTIONS" icon="sell" iconColorClass="text-blue" [value]="fmtInt(totalMentions)" [deltaLabel]="mentionsFootnote" [sampleSize]="totalMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.BRANDS' | translate" infoKey="ANALYTICS_INFO.BRANDS.MONITORED_BRANDS" icon="business" iconColorClass="text-blue" [value]="monitoredBrandsCount.toString()" [deltaLabel]="monitoredBrandsFootnote"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.NET_SENTIMENT' | translate" infoKey="ANALYTICS_INFO.COMMON.NET_SENTIMENT" icon="auto_awesome" iconColorClass="text-ink-subtle" [value]="netSentimentValue" [deltaLabel]="netSentimentFootnote" [sampleSize]="focusBrandMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_BRANDS.SOV_VOLUME_LABEL' | translate" infoKey="ANALYTICS_INFO.COMMON.SHARE_OF_VOICE" icon="layers" iconColorClass="text-blue" [value]="sovValue" [deltaLabel]="sovFootnote" [sampleSize]="focusBrandMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX' | translate" infoKey="ANALYTICS_INFO.COMMON.VISIBILITY" icon="bar_chart" iconColorClass="text-ink-subtle" [value]="avgReachValue" [deltaLabel]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate" [sampleSize]="focusBrandMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE' | translate" infoKey="ANALYTICS_INFO.COMMON.IMPACT" icon="check_circle" iconColorClass="text-ink-subtle" [value]="avgImpactValue" [deltaLabel]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate" [sampleSize]="focusBrandMentions"></app-kpi-tile>
	</div>

	<!-- 3. MAIN WIDGETS GRID -->
	<div class="space-y-4">
		<!-- ROW 1: Widget 1 (col-6) + Widget 2 (col-3) + Widget 3 (col-3) -->
		<div class="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
			<!-- WIDGET 1: Brand Comparison (Overview) -->
			<div class="xl:col-span-6 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<div class="flex items-center justify-between mb-2">
						<h3 class="section-title flex items-center gap-1">1. {{ 'ANALYTICS_BRANDS.BRAND_COMPARISON_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.BRAND_COMPARISON"></app-info-icon></h3>
						<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_BRANDS.MONITORED_BRANDS_COUNT' | translate : { count: monitoredBrandsCount } }}</span>
					</div>

					<div class="overflow-x-auto">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.BRAND' | translate }}</th>
									<th class="pb-2">{{ 'ANALYTICS_BRANDS.ROLE_LABEL' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_BRANDS.SOV_HEADER' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_BRANDS.COL_AVG_VISIBILITY' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_BRANDS.COL_AVG_IMPACT' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.NET_SENTIMENT' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (b of brandTableData; track b.brand) {
								<tr class="border-t border-line transition-colors" [class]="b.isOurBrand ? 'bg-blue-tint' : 'hover:bg-surface-blue'">
									<td class="py-2.5">
										<div class="flex items-center gap-1.5 font-medium text-ink">
											<span class="w-2.5 h-2.5 rounded-sm shrink-0" [style.background-color]="b.color"></span>
											<span>{{ b.brand }}</span>
										</div>
									</td>
									<td class="py-2.5">
										<span class="px-1.5 py-0.5 rounded-full text-[9px] font-medium" [class]="b.isOurBrand ? 'bg-blue text-on-blue' : 'bg-surface-gray text-ink-muted'">
											{{ (b.isOurBrand ? 'ANALYTICS_GLOSSARY.OUR_BRAND' : 'ANALYTICS_GLOSSARY.COMPETITION') | translate }}
										</span>
									</td>
									<td class="py-2.5 text-right font-medium text-ink tabular-nums">{{ b.mentions }}</td>
									<td class="py-2.5 text-right font-medium text-ink tabular-nums">{{ b.sov }}</td>
									<td class="py-2.5 text-right font-medium text-ink-muted tabular-nums">{{ b.avgReach }}</td>
									<td class="py-2.5 text-right font-medium text-ink-muted tabular-nums">{{ b.avgImpact }}</td>
									<td class="py-2.5 text-right">
										<span class="font-medium px-1.5 py-0.5 rounded-full text-[10px] tabular-nums" [class]="toneClasses(b.netSentimentTone)">{{ b.netSentiment }}</span>
									</td>
								</tr>
								}
								@if (!loading && brandTableData.length === 0) {
								<tr><td colspan="7" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_BRANDS.NO_BRAND_DATA' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- WIDGET 2: Mentions Trend by Brand -->
			<div class="xl:col-span-3 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">2. {{ 'ANALYTICS_BRANDS.MENTIONS_TREND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.MENTIONS_TREND_BY_BRAND"></app-info-icon></h3>
					<div class="flex flex-wrap items-center gap-2 mb-2 caption">
						@for (l of trendLegend; track l.name) {
						<span class="flex items-center gap-1 text-ink-muted font-normal"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="l.color"></span> {{ l.name }}</span>
						}
					</div>
				</div>

				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="line" [data]="mentionsTrendChartData" [options]="mentionsTrendChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 3: Share of Voice (SOV) by Brand -->
			<div class="xl:col-span-3 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">3. {{ 'ANALYTICS_BRANDS.SOV_BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.SOV_BY_BRAND"></app-info-icon></h3>
					<div class="flex flex-wrap items-center gap-x-2.5 gap-y-1 mb-2 caption">
						@for (l of sovLegend; track l.name) {
						<span class="flex items-center gap-1 font-normal text-ink-muted"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="l.color"></span> {{ l.name }} ({{ l.sov }})</span>
						}
					</div>
				</div>

				<div class="relative w-full min-h-[250px] h-full flex-1 flex items-center justify-center">
					<app-chart type="doughnut" [data]="sovDoughnutChartData" [options]="sovDoughnutChartOptions" height="100%"></app-chart>
					<div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
						<span class="text-xl font-medium text-ink tabular-nums">{{ sovValue }}</span>
						<span class="overline-label text-ink-subtle">{{ 'ANALYTICS_BRANDS.OUR_SHARE_LABEL' | translate }}</span>
					</div>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1 text-center" [count]="totalMentions"></app-sample-size-note>
				}
			</div>
		</div>

		<!-- ROW 2 (4 columns): Widgets 4, 5, 6, 7 -->
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
			<!-- WIDGET 4: Avg Visibility Index by Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
				<h3 class="section-title mb-2 flex items-center gap-1">4. {{ 'ANALYTICS_BRANDS.AVG_VISIBILITY_BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.AVG_VISIBILITY_BY_BRAND"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="reachBarChartData" [options]="reachBarChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 5: Avg Impact Score by Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
				<h3 class="section-title mb-2 flex items-center gap-1">5. {{ 'ANALYTICS_BRANDS.AVG_IMPACT_BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.AVG_IMPACT_BY_BRAND"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="impactBarChartData" [options]="impactBarChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 6: Net Sentiment (%) by Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
				<h3 class="section-title mb-2 flex items-center gap-1">6. {{ 'ANALYTICS_BRANDS.NET_SENTIMENT_BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.NET_SENTIMENT_BY_BRAND"></app-info-icon></h3>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="netSentimentChartData" [options]="netSentimentChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 7: Mentions by Media Type -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[340px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">7. {{ 'ANALYTICS_BRANDS.MENTIONS_BY_MEDIA_TYPE_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.MENTIONS_BY_MEDIA_TYPE"></app-info-icon></h3>
					<div class="flex items-center gap-2 mb-1.5 caption">
						@for (l of mediaTypeLegend; track l.label) {
						<span class="flex items-center gap-1 text-ink-muted font-normal"><span class="w-2 h-2 rounded-[3px]" [style.background-color]="l.color"></span> {{ l.label }}</span>
						}
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="bar" [data]="mediaStackedChartData" [options]="mediaStackedChartOptions" height="100%"></app-chart>
				</div>
				@if (totalMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="totalMentions"></app-sample-size-note>
				}
			</div>
		</div>

		<!-- ROW 3 (12 columns): Widgets 8, 9, 10 -->
		<div class="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
			<!-- WIDGET 8: Top Media per Brand -->
			<div class="lg:col-span-4 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">8. {{ 'ANALYTICS_BRANDS.TOP_MEDIA_PER_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.TOP_MEDIA_PER_BRAND"></app-info-icon></h3>
					<div class="overflow-x-auto">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-2">{{ 'ANALYTICS_BRANDS.COL_MEDIA_OUTLET' | translate }}</th>
									<th class="pb-2">{{ 'ANALYTICS_BRANDS.COL_TYPE' | translate }}</th>
									<th class="pb-2">{{ 'ANALYTICS_BRANDS.COL_TOP_BRAND' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_BRANDS.COL_SHARE' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (item of topMediaOutlets; track item.outlet) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-2 font-medium text-ink">{{ item.outlet }}</td>
									<td class="py-2"><span class="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-blue-tint text-blue">{{ item.type }}</span></td>
									<td class="py-2"><span class="font-medium text-ink">{{ item.topBrand }}</span></td>
									<td class="py-2 text-right font-medium text-ink tabular-nums">{{ item.mentions }}</td>
									<td class="py-2 text-right text-ink-subtle font-medium tabular-nums">{{ item.share }}</td>
								</tr>
								}
								@if (!loading && topMediaOutlets.length === 0) {
								<tr><td colspan="5" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_BRANDS.NO_OUTLET_DATA' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- WIDGET 9: Most Impactful Mentions by Brand -->
			<div class="lg:col-span-4 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">9. {{ 'ANALYTICS_BRANDS.MOST_IMPACTFUL_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.MOST_IMPACTFUL_MENTIONS"></app-info-icon></h3>
					<div class="space-y-2">
						@for (story of impactfulStories; track story.id) {
						<div class="p-2.5 rounded-xl border border-line">
							<div class="flex items-center justify-between gap-2 mb-1">
								<div class="flex items-center gap-1.5">
									<span class="font-medium px-1.5 py-0.5 rounded-full text-[9px]" [class]="story.isOurBrand ? 'bg-blue text-on-blue' : 'bg-surface-gray text-ink-muted'">{{ story.brand }}</span>
									<span class="text-ink-subtle text-[10px] font-medium">{{ story.media }}</span>
								</div>
								<span class="text-ink-subtle text-[9px]">{{ story.dateLabel }}</span>
							</div>
							<h4 class="text-xs font-medium text-ink line-clamp-1 leading-snug">{{ story.headline }}</h4>
							<div class="flex items-center justify-end mt-1.5 caption text-ink-subtle font-normal gap-3">
								<span>{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}: <strong class="text-ink-muted font-medium">{{ story.reach }}</strong></span>
								<span class="font-medium px-1.5 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(story.impactTone)">{{ story.impact }} pts</span>
							</div>
						</div>
						}
						@if (!loading && impactfulStories.length === 0) {
						<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_BRANDS.NO_IMPACTFUL_DATA' | translate }}</div>
						}
					</div>
				</div>
			</div>

			<!-- WIDGET 10: Rate of Change -->
			<div class="lg:col-span-4 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">10. {{ 'ANALYTICS_BRANDS.RATE_OF_CHANGE_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.RATE_OF_CHANGE"></app-info-icon></h3>
					<div class="overflow-x-auto">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.BRAND' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (row of rateOfChangeData; track row.brand) {
								<tr class="border-t border-line transition-colors" [class]="row.isOurBrand ? 'bg-blue-tint' : 'hover:bg-surface-blue'">
									<td class="py-2.5">
										<div class="flex items-center gap-1.5">
											<span class="font-medium text-ink">{{ row.brand }}</span>
											@if (row.isOurBrand) {
											<span class="text-[8px] bg-blue text-on-blue px-1 py-0.2 rounded-full font-medium">{{ 'ANALYTICS_GLOSSARY.CLIENT' | translate }}</span>
											}
										</div>
									</td>
									<td class="py-2.5 text-right font-medium tabular-nums"><span [class]="toneClasses(row.mentionsChg.tone)" class="px-1 py-0.2 rounded-full">{{ row.mentionsChg.text }}</span></td>
									<td class="py-2.5 text-right font-medium tabular-nums"><span [class]="toneClasses(row.reachChg.tone)" class="px-1 py-0.2 rounded-full">{{ row.reachChg.text }}</span></td>
									<td class="py-2.5 text-right font-medium tabular-nums"><span [class]="toneClasses(row.impactChg.tone)" class="px-1 py-0.2 rounded-full">{{ row.impactChg.text }}</span></td>
								</tr>
								}
								@if (!loading && rateOfChangeData.length === 0) {
								<tr><td colspan="4" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_BRANDS.SELECT_COMPARISON_PERIOD' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>

		<!-- ROW 4 (full width): Widget 11 Competitive Topic Positioning -->
		<div class="bg-surface p-4 sm:p-5 rounded-2xl border border-line space-y-3.5">
			<div class="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-line">
				<div>
					<h3 class="section-title flex items-center gap-1">11. {{ 'ANALYTICS_BRANDS.COMPETITIVE_TOPIC_POSITIONING_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.BRANDS.COMPETITIVE_TOPIC_POSITIONING"></app-info-icon></h3>
					<p class="caption text-ink-subtle font-normal mt-0.5">{{ 'ANALYTICS_BRANDS.TOPIC_POSITIONING_DESC' | translate }}</p>
				</div>

				<div class="flex flex-wrap items-center gap-3">
					<div class="flex items-center gap-1.5 text-xs">
						<label for="competitor-compare-select" class="text-ink-subtle font-medium shrink-0 text-[11px]">{{ 'ANALYTICS_BRANDS.COMPARE_AGAINST_LABEL' | translate }}:</label>
						<div class="relative">
							<select
								id="competitor-compare-select"
								[(ngModel)]="selectedCompetitorTagId"
								class="bg-surface-gray border border-line-strong rounded-full pl-2.5 pr-7 h-8 text-xs font-medium text-ink focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 cursor-pointer appearance-none">
								@for (c of competitorOptions; track c.tagId) {
								<option [value]="c.tagId">{{ 'ANALYTICS_BRANDS.COMPETITOR_OPTION_LABEL' | translate : { name: c.name, sov: c.sov } }}</option>
								}
							</select>
							<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle absolute right-2 top-2 pointer-events-none">expand_more</mat-icon>
						</div>
					</div>
				</div>
			</div>

			<div class="space-y-2">
				@for (row of sortedCompetitiveTopics; track row.topic) {
				<div class="grid grid-cols-12 gap-2 sm:gap-4 items-center p-2 rounded-xl border transition-colors" [class]="topicRowClass(row)">
					<div class="col-span-4 sm:col-span-3 lg:col-span-2 flex items-center gap-1.5 min-w-0">
						<span class="font-medium text-ink text-xs truncate" [title]="row.topic">{{ row.topic }}</span>
					</div>

					<div class="col-span-5 sm:col-span-6 lg:col-span-8 space-y-1 relative pr-2">
						<div class="relative flex items-center">
							<div class="w-full bg-neutral-tint rounded-full h-3.5 sm:h-4 overflow-hidden flex">
								<div class="bg-blue h-full rounded-full transition-all duration-300 flex items-center justify-end pr-1.5 text-[9px] font-medium text-on-blue" [style.width.%]="row.focusShare < 8 ? 8 : row.focusShare">
									<span>{{ row.focusShare }}%</span>
								</div>
							</div>
						</div>
						<div class="relative flex items-center">
							<div class="w-full bg-neutral-tint rounded-full h-3.5 sm:h-4 overflow-hidden flex">
								<div class="h-full rounded-full transition-all duration-300 flex items-center justify-end pr-1.5 text-[9px] font-medium text-on-blue" [style.width.%]="row.compShare < 8 ? 8 : row.compShare" [style.background-color]="selectedCompetitorColor">
									<span>{{ row.compShare }}%</span>
								</div>
							</div>
						</div>
					</div>

					<div class="col-span-3 sm:col-span-3 lg:col-span-2 flex justify-end">
						@if (topicIsEven(row)) {
						<span class="bg-surface-gray text-ink-muted px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap">{{ 'ANALYTICS_BRANDS.PARITY_LABEL' | translate : { gap: topicGap(row) < 0 ? -topicGap(row) : topicGap(row) } }}</span>
						} @else if (topicFocusLeads(row)) {
						<span class="bg-blue-tint text-blue px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 whitespace-nowrap">
							<span class="w-1.5 h-1.5 rounded-full bg-blue shrink-0"></span>
							<span>+{{ topicGap(row) }} pp</span>
						</span>
						} @else {
						<span class="bg-surface-gray text-ink-muted px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 whitespace-nowrap">
							<span class="w-1.5 h-1.5 rounded-full shrink-0" [style.background-color]="selectedCompetitorColor"></span>
							<span>+{{ -topicGap(row) }} pp {{ selectedCompetitorName }}</span>
						</span>
						}
					</div>
				</div>
				}
				@if (!loading && sortedCompetitiveTopics.length === 0) {
				<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_BRANDS.NO_TOPIC_DATA' | translate }}</div>
				}
			</div>

			@if (sortedCompetitiveTopics.length) {
			<div class="flex items-center gap-2 pt-2.5 border-t border-line caption">
				<mat-icon class="!w-3.5 !h-3.5 !text-sm text-blue shrink-0">auto_awesome</mat-icon>
				<span class="font-medium text-ink">{{ dynamicFooterSummary }}</span>
			</div>
			}
		</div>
	</div>

	<!-- 4. FOOTER NOTE -->
	<div class="text-center py-2.5 px-4 bg-surface-gray rounded-2xl border border-line text-ink-subtle caption font-normal">
		<span>{{ 'ANALYTICS_BRANDS.FOOTER_NOTE' | translate }}</span>
	</div>
	</div>
	}

	<!-- MANAGE BRANDS MODAL - hidden 2026-10-06 (product decision 2026-10-05): not in v1 -->
	@if (false && showManageModal) {
	<div class="fixed inset-0 bg-ink/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl border border-line shadow-pop max-w-lg w-full p-5 space-y-4">
			<div class="flex items-center justify-between border-b border-line pb-3">
				<div class="flex items-center gap-2">
					<mat-icon class="!w-4 !h-4 !text-base text-blue">business</mat-icon>
					<h3 class="item-title text-ink">Manage Monitored Brands</h3>
				</div>
				<button type="button" (click)="closeManageModal()" class="text-ink-subtle hover:text-ink p-1 rounded-lg hover:bg-surface-gray cursor-pointer">
					<mat-icon class="!w-4 !h-4 !text-base">close</mat-icon>
				</button>
			</div>

			<p class="text-ink-subtle text-xs">Your currently selected Company/Competitor tags. Keyword/role configuration lives in Settings, not here.</p>

			<div class="space-y-2 max-h-64 overflow-y-auto pr-1">
				@for (brand of brandTableData; track brand.brand) {
				<div class="flex items-center justify-between p-2.5 rounded-xl border border-line bg-surface-gray">
					<div class="flex items-center gap-2.5">
						<span class="w-3.5 h-3.5 rounded-sm" [style.background-color]="brand.color"></span>
						<div class="font-medium text-ink">{{ brand.brand }}</div>
					</div>
					<span class="px-2 py-0.5 rounded-full text-[10px] font-medium" [class]="brand.isOurBrand ? 'bg-blue text-on-blue' : 'bg-surface-gray text-ink-muted'">
						{{ brand.isOurBrand ? 'Our Brand' : 'Competition' }}
					</span>
				</div>
				}
			</div>

			<div class="flex items-center justify-between pt-3 border-t border-line">
				<button type="button" (click)="addCompetitorBrand()" class="flex items-center gap-1 text-blue font-medium text-xs hover:underline cursor-pointer">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm">add</mat-icon>
					<span>Add Competitor Brand</span>
				</button>
				<button type="button" (click)="closeManageModal()" class="h-10 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full text-xs transition-colors cursor-pointer">Done</button>
			</div>
		</div>
	</div>
	}
</div>
